import TruncatingText from '@app/src/components/Ui/TruncatingText'
import { ensureWebsiteClickability, normalizeWebsiteDisplay } from '@app/src/utils/helpers'
import { AutoAwesomeOutlined } from '@mui/icons-material'
import type { TableCellProps, TypographyProps } from '@mui/material'
import { Link, Stack, Tooltip, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import ParentCellOrFragment from './ParentCellOrFragment'

export type EnrichedValueCellProps = {
  value?: string | number | null
  isEnriched?: boolean
  disableCell?: boolean
  width?: TableCellProps['width']
  disabled?: boolean
  isLink?: boolean
} & Omit<TypographyProps, 'children'>

const EnrichedValueCell: React.FC<EnrichedValueCellProps> = ({
  value,
  isEnriched = false,
  disableCell,
  width,
  disabled,
  isLink = false,
  ...props
}) => {
  const { formatMessage } = useIntl()

  const rawValue = useMemo(() => value?.toString() || '', [value])
  const displayValue = useMemo(() => {
    if (!rawValue) {
      return ''
    }
    if (!isLink) {
      return rawValue
    }
    return normalizeWebsiteDisplay(rawValue) ?? rawValue
  }, [isLink, rawValue])
  const linkHref = useMemo(() => (isLink ? ensureWebsiteClickability(rawValue) : undefined), [isLink, rawValue])

  if (!displayValue) {
    return (
      <ParentCellOrFragment disableCell={disableCell} width={width}>
        <Typography variant="body1" component="span" color="textSecondary" {...props}>
          -
        </Typography>
      </ParentCellOrFragment>
    )
  }

  return (
    <ParentCellOrFragment disableCell={disableCell} width={width}>
      <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
        {isEnriched && (
          <Tooltip title={formatMessage({ id: 'general.aiEnriched' })}>
            <AutoAwesomeOutlined sx={{ fontSize: 16, color: 'communication.dark', flexShrink: 0 }} />
          </Tooltip>
        )}
        <TruncatingText
          text={displayValue}
          variant="body1"
          color={disabled ? 'textSecondary' : 'textPrimary'}
          sx={{ flexGrow: 1 }}
          {...props}
        >
          {isLink ? (
            <Link href={linkHref} underline="hover" rel="noopener noreferrer" target="_blank">
              {displayValue}
            </Link>
          ) : (
            displayValue
          )}
        </TruncatingText>
      </Stack>
    </ParentCellOrFragment>
  )
}

export default EnrichedValueCell
